import React, { useState, useEffect, useRef } from 'react';
import { X, Users, Utensils, CreditCard, CheckCircle2, Calculator, Minus, Plus, Search, User, UserPlus, QrCode } from 'lucide-react';

import { useCustomers } from '../context/CustomerContext';
import { useAlert } from '../context/AlertContext';
import { supabase } from '../supabase/config';
import { orderService } from '../services/orderService';
import LinkCustomerModal from './LinkCustomerModal';
import QRScanner from './QRScanner';

const SplitPaymentModal = ({ order, onClose, onPaymentSuccess, tenantData }) => {
    const { showAlert } = useAlert();
    const [mode, setMode] = useState('equal'); // 'equal' or 'item'
    const [peopleCount, setPeopleCount] = useState(1);
    const [selectedItems, setSelectedItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const { customers } = useCustomers();
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [showCustomerSearch, setShowCustomerSearch] = useState(false);
    const [isScanning, setIsScanning] = useState(false);
    const [showAnonWarning, setShowAnonWarning] = useState(false);
    const [pendingPayment, setPendingPayment] = useState(null);
    const [showLinkCustomerModal, setShowLinkCustomerModal] = useState(false);
    const scannerRef = useRef(null);

    useEffect(() => {
        if (order.customerId && !selectedCustomer) {
            const cust = customers.find(c => c.id === order.customerId) || { id: order.customerId, firstName: order.customerName || '', lastName: '' };
            setSelectedCustomer(cust);
        }
    }, [order.customerId, customers]);

    const remainingAmount = order.remainingAmount !== undefined ? order.remainingAmount : order.totalAmount;
    const [paymentAmount, setPaymentAmount] = useState(remainingAmount);

    const getTableDisplayName = (tableId) => {
        if (!tableId || tableId === 'Manuel') return 'Manuel Sipariş';
        if (tableId === 'Paket') return 'Paket Servis / Gel-Al';
        const table = tenantData?.tables?.find(t => String(t.id) === String(tableId));
        if (table) return table.name;
        return String(tableId).startsWith('Masa') ? tableId : `Masa ${tableId}`;
    };

    const filteredCustomers = customers.filter(c =>
        c.firstName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        c.lastName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        c.phone?.includes(searchQuery)
    ).slice(0, 5);

    useEffect(() => {
        setPaymentAmount(remainingAmount);
    }, [remainingAmount]);

    const [qrError, setQrError] = useState(null);



    // Item-based calculation
    const selectedTotal = selectedItems.reduce((sum, item) => sum + (parseFloat(item.price) * item.selectedQty), 0);

    const handleEqualPayment = async () => {
        const amount = parseFloat(paymentAmount);
        if (isNaN(amount) || amount <= 0) {
            showAlert("Lütfen geçerli bir tutar giriniz.", "warning");
            return;
        }
        await processPartialPayment(amount, `Parçalı Ödeme - ₺${amount.toFixed(2)}`);
    };

    const handleQuickSplit = (count) => {
        setPeopleCount(count);
        setPaymentAmount((remainingAmount / count).toFixed(2));
    };

    const handleItemPayment = async () => {
        if (selectedTotal <= 0) return;
        const itemsForPayment = selectedItems.filter(i => i.selectedQty > 0).map(i => ({
            name: i.name,
            price: i.price,
            quantity: i.selectedQty,
            image: i.image
        }));
        await processPartialPayment(selectedTotal, 'Ürün Bazlı Ödeme', undefined, itemsForPayment);
    };

    const [successMessage, setSuccessMessage] = useState(null);

    const processPartialPayment = async (amount, type, forcedCustomer = undefined, paymentItems = null) => {
        const activeCustomer = forcedCustomer !== undefined ? forcedCustomer : selectedCustomer;

        if (!activeCustomer && !showAnonWarning) {
            setPendingPayment({ amount, type, paymentItems });
            setShowLinkCustomerModal(true);
            return;
        }

        setShowAnonWarning(false);
        setShowLinkCustomerModal(false);
        setPendingPayment(null);

        setLoading(true);
        setSuccessMessage(null);
        try {
            const newRemaining = Math.max(0, remainingAmount - amount);
            const isFullyPaid = newRemaining < 0.01;

            // Record this payment in order history
            const paymentRecord = {
                amount,
                type,
                timestamp: new Date().toISOString(),
                customerId: activeCustomer?.id || null,
                customerName: activeCustomer ? `${activeCustomer.firstName || ''} ${activeCustomer.lastName || ''}`.trim() : 'Guest',
                ...(paymentItems && paymentItems.length > 0 ? { items: paymentItems } : {})
            };

            // Read current payments array and append
            const { data: currentOrder } = await supabase.from('orders').select('payments').eq('id', order.id).single();
            const updatedPayments = [...(currentOrder?.payments || []), paymentRecord];

            const updatePayload = {
                remaining_amount: newRemaining,
                payments: updatedPayments,
                status: isFullyPaid ? 'paid' : order.status,
                updated_at: new Date().toISOString()
            };

            if (isFullyPaid) {
                updatePayload.paid_at = new Date().toISOString();
            }

            // Link customer to order if not already linked
            if (activeCustomer) {
                const { data: freshOrder } = await supabase.from('orders').select('customer_id').eq('id', order.id).single();
                if (!freshOrder?.customer_id) {
                    const fullName = `${activeCustomer.firstName || ''} ${activeCustomer.lastName || ''}`.trim() || 'Misafir';
                    updatePayload.customer_id = activeCustomer.id;
                    updatePayload.customer_name = fullName;
                }
            }

            await supabase.from('orders').update(updatePayload).eq('id', order.id);

            // 1. AWARD POINTS to the selected customer if any
            if (activeCustomer) {
                const points = tenantData?.loyaltyRules?.purchasePointsEnabled !== false
                    ? Math.floor(amount * (tenantData?.loyaltyRules?.purchasePointsMultiplier || 0.1))
                    : 0;

                const { data: customerData } = await supabase.from('customers').select('*').eq('id', activeCustomer.id).single();

                if (customerData) {
                    const now = new Date();
                    const twoYearsAgo = new Date();
                    twoYearsAgo.setMonth(twoYearsAgo.getMonth() - 24);

                    const updatedPointEarnings = [
                        ...(customerData.point_earnings || []).filter(e => new Date(e.t) > twoYearsAgo),
                        { p: points, t: now.toISOString(), type: 'Partial Payment', orderId: order.id }
                    ];

                    await supabase.from('customers').update({
                        points: (customerData.points || 0) + points,
                        total_points: (customerData.total_points || 0) + points,
                        total_spent: (customerData.total_spent || 0) + amount,
                        point_earnings: updatedPointEarnings,
                        last_interaction_at: new Date().toISOString()
                    }).eq('id', activeCustomer.id);

                    // Update earned_points on the order (accumulate across split payments)
                    const { data: currentOrderPts } = await supabase.from('orders').select('earned_points').eq('id', order.id).single();
                    await supabase.from('orders').update({
                        earned_points: (currentOrderPts?.earned_points || 0) + points
                    }).eq('id', order.id);
                }
            }

            // 3. If fully paid, trigger the standard paid logic (but avoid double logic if possible)
            if (isFullyPaid) {
                // We create a sales record for this FINAL PIECE
                await supabase.from('sales').insert({
                    tenant_id: order.tenantId,
                    order_id: order.id,
                    customer_id: activeCustomer?.id || null,
                    customer_name: activeCustomer ? `${activeCustomer.firstName || ''} ${activeCustomer.lastName || ''}`.trim() : 'Guest',
                    amount: amount,
                    type: 'Final Payment',
                    timestamp: new Date().toISOString()
                });
            } else {
                // Just log activity for partial payment
                await supabase.from('sales').insert({
                    tenant_id: order.tenantId,
                    order_id: order.id,
                    customer_id: activeCustomer?.id || null,
                    customer_name: activeCustomer ? `${activeCustomer.firstName || ''} ${activeCustomer.lastName || ''}`.trim() : 'Guest',
                    amount: amount,
                    type: 'Partial Payment',
                    timestamp: new Date().toISOString()
                });

                setSuccessMessage(`₺${amount.toFixed(2)} Tahsil Edildi! ${activeCustomer ? `(${activeCustomer.firstName || ''} kazandı)` : ''}`);
                setTimeout(() => setSuccessMessage(null), 3000);
            }

            if (onPaymentSuccess) onPaymentSuccess(isFullyPaid);

            if (isFullyPaid) {
                onClose();
            } else {
                setSelectedItems([]);
                setSelectedCustomer(null);
                setSearchQuery('');
            }
        } catch (err) {
            console.error("Payment error:", err);
            showAlert("Ödeme alınırken bir hata oluştu.", "error");
        } finally {
            setLoading(false);
        }
    };

    const toggleItemSelection = (item, qty) => {
        setSelectedItems(prev => {
            const existing = prev.find(i => i.prodId === item.prodId);
            if (existing) {
                const newQty = existing.selectedQty + qty;
                if (newQty <= 0) return prev.filter(i => i.prodId !== item.prodId);
                if (newQty > item.quantity) return prev; // Cannot select more than ordered
                return prev.map(i => i.prodId === item.prodId ? { ...i, selectedQty: newQty } : i);
            }
            return [...prev, { ...item, selectedQty: 1 }];
        });
    };

    return (
        <div onClick={onClose} style={{
            position: 'fixed', inset: 0, zIndex: 2000,
            background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(10px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem'
        }}>
            <div onClick={(e) => e.stopPropagation()} className="card glass-premium" style={{
                width: '100%', maxWidth: '500px', maxHeight: '90vh',
                background: 'var(--bg-main)', overflow: 'hidden',
                display: 'flex', flexDirection: 'column'
            }}>
                <div style={{ padding: '1.5rem', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <div style={{ width: '40px', height: '40px', borderRadius: '12px', background: 'var(--primary)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white' }}>
                            <Calculator size={22} />
                        </div>
                        <div>
                            <h3 style={{ fontWeight: 800, fontSize: '1.25rem' }}>Ödemeyi Böl</h3>
                            <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', fontWeight: 600 }}>{getTableDisplayName(order.tableNumber)} • Kalan: ₺{remainingAmount.toFixed(2)}</p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        style={{
                            background: 'rgba(0,0,0,0.05)',
                            border: 'none',
                            cursor: 'pointer',
                            padding: '8px',
                            borderRadius: '10px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'var(--text-main)',
                            transition: 'all 0.2s'
                        }}
                        onMouseOver={(e) => e.currentTarget.style.background = 'rgba(239, 68, 68, 0.1)'}
                        onMouseOut={(e) => e.currentTarget.style.background = 'rgba(0,0,0,0.05)'}
                    >
                        <X size={20} strokeWidth={2.5} />
                    </button>
                </div>

                <div style={{ display: 'flex', borderBottom: '1px solid var(--border)', background: 'var(--bg-card)' }}>
                    <button
                        onClick={() => setMode('equal')}
                        style={{
                            flex: 1, padding: '1.25rem 1rem', border: 'none', background: mode === 'equal' ? 'rgba(var(--primary-rgb), 0.08)' : 'transparent',
                            color: mode === 'equal' ? 'var(--primary)' : 'var(--text-muted)', fontWeight: 800, cursor: 'pointer',
                            borderBottom: mode === 'equal' ? '3px solid var(--primary)' : '3px solid transparent',
                            transition: 'all 0.2s', fontSize: '0.9375rem',
                            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px'
                        }}
                    >
                        <Users size={20} />
                        Böl
                    </button>
                    <button
                        onClick={() => setMode('item')}
                        style={{
                            flex: 1, padding: '1.25rem 1rem', border: 'none', background: mode === 'item' ? 'rgba(var(--primary-rgb), 0.08)' : 'transparent',
                            color: mode === 'item' ? 'var(--primary)' : 'var(--text-muted)', fontWeight: 800, cursor: 'pointer',
                            borderBottom: mode === 'item' ? '3px solid var(--primary)' : '3px solid transparent',
                            transition: 'all 0.2s', fontSize: '0.9375rem',
                            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px'
                        }}
                    >
                        <Utensils size={20} />
                        Ürün Bazlı
                    </button>
                </div>

                <div style={{ padding: '1.5rem', overflowY: 'auto', flex: 1, minHeight: '300px', position: 'relative' }}>
                    {successMessage && (
                        <div style={{
                            position: 'absolute', top: '1rem', left: '1rem', right: '1rem', zIndex: 10,
                            background: '#10b981', color: 'white', padding: '12px', borderRadius: '12px',
                            display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 700,
                            boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)', animation: 'slideDown 0.3s ease'
                        }}>
                            <CheckCircle2 size={18} />
                            {successMessage}
                        </div>
                    )}
                    {mode === 'equal' ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                            {/* Manual Amount Input - Main Focus */}
                            <div style={{ textAlign: 'center', padding: '0.5rem 0' }}>
                                <div style={{ marginBottom: '1.25rem' }}>
                                    <span style={{
                                        padding: '6px 16px',
                                        background: 'rgba(var(--primary-rgb), 0.08)',
                                        borderRadius: '20px',
                                        fontSize: '0.8125rem',
                                        fontWeight: 800,
                                        color: 'var(--primary)'
                                    }}>
                                        Kalan: ₺{remainingAmount.toFixed(2)}
                                    </span>
                                </div>
                                <p style={{ marginBottom: '1rem', fontWeight: 800, color: 'var(--text-muted)', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '1px' }}>Tahsil Edilecek Tutar</p>
                                <div style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <span style={{ position: 'absolute', left: '20px', fontSize: '2rem', fontWeight: 950, color: 'var(--primary)' }}>₺</span>
                                    <input
                                        type="number"
                                        value={paymentAmount}
                                        onChange={(e) => setPaymentAmount(e.target.value)}
                                        style={{
                                            width: '100%',
                                            padding: '1.5rem 1.5rem 1.5rem 3.5rem',
                                            fontSize: '3rem',
                                            fontWeight: 950,
                                            textAlign: 'center',
                                            borderRadius: '24px',
                                            border: '2px solid var(--primary)',
                                            background: 'white',
                                            color: 'var(--text-main)',
                                            outline: 'none',
                                            boxShadow: '0 10px 30px rgba(var(--primary-rgb), 0.1)'
                                        }}
                                    />
                                </div>
                            </div>

                            {/* Equal Split Section - Compact & Subtle at the bottom of this area */}
                            <div className="equal-split-row" style={{
                                marginTop: '1rem',
                                padding: '1rem 1.25rem',
                                background: 'rgba(var(--primary-rgb), 0.02)',
                                borderRadius: '20px',
                                border: '1px dashed var(--border)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: '1rem'
                            }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexShrink: 0 }}>
                                    <div style={{ width: '32px', height: '32px', borderRadius: '8px', background: 'white', border: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--primary)' }}>
                                        <Users size={16} />
                                    </div>
                                    <span style={{ fontWeight: 700, fontSize: '0.75rem', color: 'var(--text-main)', whiteSpace: 'nowrap' }}>Eşit Paylaştır</span>
                                </div>

                                <div className="people-counter" style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '1rem',
                                    background: 'rgba(var(--primary-rgb), 0.05)',
                                    padding: '6px 14px',
                                    borderRadius: '16px',
                                    border: '1px solid rgba(var(--primary-rgb), 0.1)',
                                    flexShrink: 0
                                }}>
                                    <button
                                        onClick={() => handleQuickSplit(Math.max(1, peopleCount - 1))}
                                        style={{ background: 'transparent', border: 'none', color: 'var(--primary)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s', padding: '4px' }}
                                        onMouseOver={(e) => e.currentTarget.style.transform = 'scale(1.2)'}
                                        onMouseOut={(e) => e.currentTarget.style.transform = 'scale(1)'}
                                    >
                                        <Minus size={20} strokeWidth={3} />
                                    </button>
                                    <div style={{ textAlign: 'center', minWidth: '35px' }}>
                                        <span style={{ fontSize: '1.125rem', fontWeight: 950, color: 'var(--primary)', display: 'block', lineHeight: 1 }}>{peopleCount}</span>
                                        <span style={{ fontSize: '0.55rem', fontWeight: 800, color: 'var(--primary)', textTransform: 'uppercase' }}>Kişi</span>
                                    </div>
                                    <button
                                        onClick={() => handleQuickSplit(peopleCount + 1)}
                                        style={{ background: 'transparent', border: 'none', color: 'var(--primary)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s', padding: '4px' }}
                                        onMouseOver={(e) => e.currentTarget.style.transform = 'scale(1.2)'}
                                        onMouseOut={(e) => e.currentTarget.style.transform = 'scale(1)'}
                                    >
                                        <Plus size={20} strokeWidth={3} />
                                    </button>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                            {order.items.map((item, idx) => {
                                const selected = selectedItems.find(i => i.prodId === item.prodId);
                                const selectedQty = selected ? selected.selectedQty : 0;

                                return (
                                    <div key={idx} style={{
                                        padding: '1.125rem', borderRadius: '20px', border: '1px solid var(--border)',
                                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                        background: selectedQty > 0 ? 'rgba(var(--primary-rgb), 0.03)' : 'var(--bg-card)',
                                        transition: 'all 0.2s',
                                        borderColor: selectedQty > 0 ? 'var(--primary)' : 'var(--border)'
                                    }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                            <div style={{ width: '40px', height: '40px', borderRadius: '12px', background: selectedQty > 0 ? 'var(--primary)' : 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', color: selectedQty > 0 ? 'white' : 'var(--primary)', transition: 'all 0.2s', border: selectedQty > 0 ? 'none' : '1px solid var(--border)' }}>
                                                <Utensils size={18} />
                                            </div>
                                            <div>
                                                <div style={{ fontWeight: 800, fontSize: '0.9375rem' }}>{item.name}</div>
                                                <div style={{ fontSize: '0.8125rem', color: 'var(--primary)', fontWeight: 700 }}>₺{item.price} <span style={{ color: 'var(--text-muted)', fontWeight: 600 }}>/ adet</span></div>
                                            </div>
                                        </div>
                                        <div style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '10px',
                                            background: 'rgba(var(--primary-rgb), 0.05)',
                                            padding: '4px 8px',
                                            borderRadius: '12px',
                                            border: '1px solid rgba(var(--primary-rgb), 0.1)',
                                            flexShrink: 0
                                        }}>
                                            <button
                                                onClick={() => toggleItemSelection(item, -1)}
                                                style={{ background: 'transparent', border: 'none', color: 'var(--primary)', cursor: 'pointer', display: 'flex', padding: '4px', transition: 'all 0.2s' }}
                                                onMouseOver={(e) => e.currentTarget.style.transform = 'scale(1.2)'}
                                                onMouseOut={(e) => e.currentTarget.style.transform = 'scale(1)'}
                                            >
                                                <Minus size={18} strokeWidth={3} />
                                            </button>
                                            <span style={{ fontWeight: 900, minWidth: '24px', textAlign: 'center', fontSize: '1rem', color: 'var(--primary)' }}>{selectedQty}</span>
                                            <button
                                                onClick={() => toggleItemSelection(item, 1)}
                                                style={{ background: 'transparent', border: 'none', color: 'var(--primary)', cursor: 'pointer', display: 'flex', padding: '4px', transition: 'all 0.2s' }}
                                                onMouseOver={(e) => e.currentTarget.style.transform = 'scale(1.2)'}
                                                onMouseOut={(e) => e.currentTarget.style.transform = 'scale(1)'}
                                            >
                                                <Plus size={18} strokeWidth={3} />
                                            </button>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                <style>{`
                    @media (max-width: 440px) {
                        .equal-split-row {
                            flex-direction: column !important;
                            gap: 0.75rem !important;
                            padding: 1rem !important;
                        }
                        .people-counter {
                            width: 100% !important;
                            justify-content: center !important;
                        }
                    }
                `}</style>

                <div style={{ padding: '1.5rem', borderTop: '2px dashed var(--border)', background: 'var(--bg-main)' }}>
                    {/* Customer Selection UI */}
                    <div style={{ marginBottom: '1.25rem', position: 'relative' }}>
                        <p style={{ fontSize: '0.75rem', fontWeight: 800, color: 'var(--text-muted)', marginBottom: '8px', textTransform: 'uppercase' }}>Ödemeyi Yapan Müşteri</p>

                        {!selectedCustomer ? (
                            <div style={{ display: 'flex', gap: '8px' }}>
                                <div style={{ position: 'relative', flex: 1 }}>
                                    <div style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }}>
                                        <Search size={16} />
                                    </div>
                                    <input
                                        type="text"
                                        placeholder="Müşteri adı veya telefon..."
                                        value={searchQuery}
                                        onChange={(e) => {
                                            setSearchQuery(e.target.value);
                                            setShowCustomerSearch(true);
                                        }}
                                        onFocus={() => setShowCustomerSearch(true)}
                                        style={{
                                            width: '100%', padding: '10px 10px 10px 38px', borderRadius: '12px',
                                            border: '1px solid var(--border)', background: 'white', fontSize: '0.875rem'
                                        }}
                                    />
                                    {showCustomerSearch && searchQuery.length > 0 && (
                                        <div style={{
                                            position: 'absolute', bottom: '100%', left: 0, right: 0,
                                            background: 'white', borderRadius: '16px', border: '1px solid var(--border)',
                                            boxShadow: '0 -10px 25px rgba(0,0,0,0.1)', zIndex: 100, marginBottom: '8px',
                                            overflow: 'hidden'
                                        }}>
                                            {filteredCustomers.length > 0 ? filteredCustomers.map(c => (
                                                <div
                                                    key={c.id}
                                                    onClick={() => {
                                                        setSelectedCustomer(c);
                                                        setShowCustomerSearch(false);
                                                    }}
                                                    style={{ padding: '12px 16px', display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', borderBottom: '1px solid var(--border)' }}
                                                    className="hover-bg"
                                                >
                                                    <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: 'var(--primary)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: '0.75rem', fontWeight: 800 }}>
                                                        {c.firstName[0]}{c.lastName?.[0] || ''}
                                                    </div>
                                                    <div>
                                                        <div style={{ fontSize: '0.875rem', fontWeight: 700 }}>{c.firstName} {c.lastName}</div>
                                                        <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{c.phone}</div>
                                                    </div>
                                                </div>
                                            )) : (
                                                <div style={{ padding: '20px', textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.875rem' }}>
                                                    Müşteri bulunamadı.
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                                <button
                                    onClick={() => setIsScanning(true)}
                                    style={{
                                        background: 'white', border: '1px solid var(--border)', borderRadius: '12px',
                                        padding: '0 12px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        color: 'var(--primary)', cursor: 'pointer'
                                    }}
                                    title="QR Tara"
                                >
                                    <QrCode size={20} />
                                </button>

                                {isScanning && (
                                    <QRScanner
                                        onScan={(id) => {
                                            const c = customers.find(x => x.id === id || x.shortId === id);
                                            if (c) {
                                                setSelectedCustomer(c);
                                                setIsScanning(false);
                                            } else {
                                                setQrError("Bu karekoda ait müşteri bulunamadı!");
                                            }
                                        }}
                                        onClose={() => setIsScanning(false)}
                                    />
                                )}
                            </div>
                        ) : (
                            <div style={{
                                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                background: 'rgba(var(--primary-rgb), 0.05)', padding: '8px 12px',
                                borderRadius: '12px', border: '1px solid var(--primary)'
                            }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                    <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: 'var(--primary)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white' }}>
                                        <User size={16} />
                                    </div>
                                    <div>
                                        <div style={{ fontSize: '0.875rem', fontWeight: 700 }}>{selectedCustomer.firstName} {selectedCustomer.lastName}</div>
                                        <div style={{ fontSize: '0.75rem', color: 'var(--primary)', fontWeight: 700 }}>Sadakat Puanı Kazanacak</div>
                                    </div>
                                </div>
                                <button
                                    onClick={() => setSelectedCustomer(null)}
                                    style={{ background: 'transparent', border: 'none', color: '#ef4444', cursor: 'pointer', padding: '4px' }}
                                >
                                    <X size={16} />
                                </button>
                            </div>
                        )}
                    </div>

                    {mode === 'item' && (
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', padding: '0 0.5rem' }}>
                            <span style={{ fontSize: '1rem', fontWeight: 800, color: 'var(--text-muted)' }}>Seçilenlerin Toplamı:</span>
                            <span style={{ fontSize: '1.75rem', fontWeight: 950, color: 'var(--primary)' }}>₺{selectedTotal.toFixed(2)}</span>
                        </div>
                    )}
                    <button
                        onClick={mode === 'equal' ? handleEqualPayment : handleItemPayment}
                        disabled={loading || (mode === 'item' && selectedTotal <= 0)}
                        style={{
                            width: '100%', padding: '1.25rem', borderRadius: '20px', border: 'none',
                            background: 'var(--primary)', color: 'white', fontWeight: 900,
                            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px',
                            cursor: 'pointer', boxShadow: '0 10px 25px rgba(var(--primary-rgb), 0.4)',
                            opacity: (loading || (mode === 'item' && selectedTotal <= 0)) ? 0.7 : 1,
                            fontSize: '1.125rem', textTransform: 'uppercase', letterSpacing: '0.5px',
                            transition: 'all 0.2s'
                        }}
                        className="hover-scale"
                    >
                        {loading ? (
                            <div className="loading-spinner" style={{ width: '24px', height: '24px', border: '3px solid white', borderTopColor: 'transparent' }}></div>
                        ) : (
                            <>
                                <CreditCard size={22} strokeWidth={2.5} />
                                {mode === 'equal' ? (
                                    parseFloat(paymentAmount) >= remainingAmount - 0.01
                                        ? 'TAMAMINI TAHSİL ET'
                                        : `₺${parseFloat(paymentAmount).toFixed(2)} TAHSİL ET`
                                ) : (
                                    selectedTotal >= remainingAmount - 0.01
                                        ? 'KALANIN TAMAMINI AL'
                                        : `SEÇİLENLERİ TAHSİL ET (₺${selectedTotal.toFixed(2)})`
                                )}
                            </>
                        )}
                    </button>


                </div>

            </div>

            {/* Render over the entire modal overlay */}
            {showLinkCustomerModal && pendingPayment && (
                <LinkCustomerModal
                    order={order}
                    onClose={() => setShowLinkCustomerModal(false)}
                    onLinked={(customer) => {
                        setShowLinkCustomerModal(false);
                        setShowAnonWarning(true);
                        if (customer) {
                            setSelectedCustomer(customer);
                        }
                        processPartialPayment(pendingPayment.amount, pendingPayment.type, customer, pendingPayment.paymentItems);
                    }}
                />
            )}
        </div >
    );
};

export default SplitPaymentModal;
